/** English strings for the Administration area (staff, roles, approvals, API credentials, audit log). The Vietnamese file must have exactly the same keys. */
export const admin = {
  area: 'Administration',

  /** Notes that change what an operator can actually do. */
  note: {
    audited: 'Everything done in this area is written to the audit log: who did it, when, and the reason they gave.',
    noSecrets: 'Secrets are never shown here — no password, no MFA code, no session token, no API key.',
    fourEyesTitle: 'Two people, one change',
    fourEyesBody:
      'The riskiest changes take two people: one asks for the change and explains why, a different one agrees to it. Nobody can agree to their own request — the platform refuses it. Until someone agrees, nothing happens.',
    selfBlocked: 'You asked for this change, so you cannot be the one who agrees to it. Ask a colleague who also holds this permission.',
    immutable: 'This log can never be edited or deleted. Entries stay exactly as they were written.',
    keyOnce: 'A key’s secret is shown once, to the person who created it. It can never be read back. If it is lost, revoke the key and issue a new one.',
  },

  filter: {
    search: 'Search',
    reset: 'Reset filters',
    refresh: 'Refresh',
    any: 'Any',
  },

  scope: {
    global: 'Whole platform',
    customer: 'Customer',
  },

  staffStatus: {
    ACTIVE: 'Active',
    SUSPENDED: 'Suspended',
    DISABLED: 'Disabled',
  },

  userKind: {
    CLIENT: 'Client',
    STAFF: 'Staff',
    CUSTOMER: 'Customer',
  },

  staff: {
    title: 'Staff accounts',
    subtitle: 'Everyone who can sign in to the Control Center, the roles they hold, and whether their sign-in is protected.',
    loadError: 'Could not load staff accounts',
    empty: 'No staff account matches these filters.',
    count: '{{count}} accounts',
    searchPlaceholder: 'Name or e-mail address',
    filterStatus: 'Account state',
    filterRole: 'Role',
    filterLocked: 'Sign-in',
    lockedOnly: 'Locked out',
    unlockedOnly: 'Can sign in',
    col: {
      person: 'Person',
      signIn: 'Sign-in',
      roles: 'Roles',
      lastLogin: 'Last signed in',
      sessions: 'Open sessions',
      state: 'State',
    },
    mfa: {
      on: 'MFA in place',
      missing: 'MFA not set up',
      missingHint: 'This account must pass MFA but has no authenticator set up yet, so it cannot sign in.',
      notRequired: 'MFA not required',
    },
    locked: 'Locked out',
    lockedUntil: 'Locked out until {{time}}',
    failedAttempts: '{{count}} failed attempts',
    neverSignedIn: 'Never',
    noRoles: 'No role',
    moreRoles: '+{{count}} more',
    open: 'Open {{name}}',
    stat: {
      total: 'Staff accounts',
      mfaMissing: 'Without MFA',
      mfaMissingHint: 'Cannot sign in until an authenticator is set up.',
      locked: 'Locked out',
      lockedHint: 'Too many failed sign-in attempts.',
      sessions: 'Open sessions',
      sessionsHint: 'Sessions that are still valid right now.',
    },
  },

  member: {
    loadError: 'Could not load this staff account',
    notFound: 'No staff account with this identifier.',
    overview: 'Account',
    email: 'E-mail address',
    state: 'Account state',
    signIn: 'Sign-in protection',
    created: 'Account created',
    lastLogin: 'Last signed in',
    failed: 'Failed sign-in attempts',
    id: 'ID',
    roles: 'Roles',
    rolesHint: 'What this person is allowed to do, and where.',
    noRoles: 'This account holds no role, so it can read and change nothing.',
    roleCol: { role: 'Role', scope: 'Applies to', granted: 'Granted' },
    sessions: 'Open sessions',
    sessionsHint: 'Sessions that are still valid. A session cannot be read back or reused from here.',
    noSessions: 'No open session.',
    sessionCol: { started: 'Started', expires: 'Expires', mfa: 'MFA', from: 'From', device: 'Device' },
    mfaVerified: 'Verified',
    mfaNotVerified: 'Not verified',
    recentAudit: 'Recent activity',
    recentAuditHint: 'The last things this person did.',
    noAudit: 'Nothing recorded for this person yet.',
    ownRequests: 'Waiting on a colleague',
    ownRequestsHint: 'Changes this person asked for. They cannot agree to any of them themselves.',
    noOwnRequests: 'No open request.',
    allActivity: 'All activity by this person',
  },

  roles: {
    title: 'Roles & permissions',
    subtitle: 'What each role is allowed to do, and who holds it.',
    loadError: 'Could not load roles',
    empty: 'No role defined.',
    count: '{{count}} roles',
    builtIn: 'Built in',
    builtInHint: 'Shipped with the platform. The software refers to this role by name.',
    col: {
      role: 'Role',
      permissions: 'Allowed to',
      sensitive: 'Needs MFA',
      fourEyes: 'Needs a second person',
      members: 'Held by',
    },
    permissionCount: '{{count}} permissions',
    memberCount: '{{count}} people',
    staffOf: '{{count}} staff',
    open: 'Open role {{role}}',
    stat: {
      roles: 'Roles',
      sensitive: 'Roles with a permission needing MFA',
      fourEyes: 'Roles with a permission needing two people',
      grants: 'Roles granted to staff',
    },
  },

  role: {
    loadError: 'Could not load this role',
    notFound: 'No role with this name.',
    about: 'Role',
    code: 'Role name',
    description: 'What it is for',
    permissions: 'Allowed to',
    permissionsHint: 'Grouped by the part of the business each permission governs.',
    noPermissions: 'This role grants nothing.',
    alsoNeeds: 'Also needs',
    needsMfa: 'Needs MFA',
    needsMfaHint: 'Only usable in a session confirmed with an authenticator code.',
    needsTwo: 'Needs a second person',
    needsTwoHint: 'One operator asks, a different operator agrees, before anything happens.',
    members: 'Held by',
    membersHint: 'Everyone who holds this role right now, and where it applies.',
    noMembers: 'Nobody holds this role.',
    memberCol: { person: 'Person', kind: 'Account', scope: 'Applies to', state: 'State', granted: 'Granted' },
    group: {
      support: 'Customer support',
      domain: 'Domains',
      subject: 'Registrants',
      identity_document: 'Identity documents',
      dossier: 'Dossiers',
      registry: 'Registries',
      billing: 'Billing & ledger',
      catalog: 'Product catalogue',
      subscription: 'Sold services',
      tax: 'Tax',
      einvoice: 'E-invoices',
      auction: 'Auctions',
      compliance: 'Compliance',
      disclosure: 'Data disclosure',
      abuse: 'Abuse',
      dispute: 'Disputes',
      dns: 'DNS service',
      escrow: 'Data escrow',
      vnnic: 'VNNIC',
      customer: 'Customer accounts',
      audit: 'Audit log',
      ops: 'Operations',
      admin: 'Administration',
      account: 'Account (client)',
      api_credential: 'API keys',
      reporting_key: 'Reporting API',
      personal_data: 'Personal data',
      addon: 'Add-ons',
      approval: 'Approvals',
      bulk: 'Bulk jobs',
      business_registry: 'Business registry',
      integration: 'Connections & integrations',
      lookup: 'Domain lookup',
      name_change: 'Registrant name change',
      notify: 'Notifications & e-mail',
      order: 'Orders',
      pricing: 'Pricing',
      promo_code: 'Promotion codes',
      reference: 'Reference lists',
      regulatory_report: 'Regulatory reports',
      service: 'Services',
      tld: 'TLDs',
      wallet: 'Wallet',
      webhook: 'Webhooks',
      work_item: 'Work queue',
    },
  },

  /**
   * The built-in catalogue: 12 roles and 41 permissions, the closed set seeded by
   * `migrations/20260925000009_seed_rbac.sql` and the seven later migrations that add
   * permissions. The API sends each one's English description; these are the operator-facing
   * names. A code is keyed with `_` for `.` because i18next splits keys on dots, and a code
   * with no entry here falls back to the description the API sent.
   */
  roleName: {
    administrator: 'Platform administrator',
    auditor: 'Read-only auditor',
    billing: 'Billing operations',
    compliance: 'Compliance officer',
    customer: 'Retail customer',
    ops_admin: 'Operations administrator',
    ops_engineer: 'Operations engineer',
    ops_viewer: 'Operations viewer',
    registry_ops: 'Registry operations',
    security: 'Security team',
    support: 'Customer support',
    client_customer_owner: 'Customer account member: OWNER',
    client_customer_admin: 'Customer account member: ADMIN',
    client_customer_billing: 'Customer account member: BILLING',
    client_customer_technical: 'Customer account member: TECHNICAL',
    client_customer_read_only: 'Customer account member: READ_ONLY',
    client_customer_key: 'API key of a customer with scopes: the most its scopes can grant',
    client_customer_key_legacy: 'API key of a customer issued without scopes (permissions frozen at client-api P0)',
  },

  permission: {
    support_create: 'Open support tickets',
    support_knowledge_manage: 'Manage the knowledge base',
    support_manage: 'Manage support tickets, departments and response deadlines',
    support_read: 'Read support tickets',
    support_reply: 'Reply to support tickets',
    domain_read: 'Read domains in scope',
    domain_write: 'Register, renew and update domains in scope',
    domain_ownership_change: 'Change the registrant of a domain',
    domain_ownership_override: 'Force a registrant change through by hand',
    domain_transfer_override: 'Force a transfer through by hand',
    domain_authinfo_view: 'Reveal a domain’s transfer code',
    subject_read: 'Read registrants, with personal data masked',
    subject_write: 'Create registrants and new registrant records',
    identity_document_view: 'View identity documents and full identity numbers',
    dossier_read: 'Read dossiers',
    dossier_review: 'Review and complete dossiers',
    registry_transaction_read: 'Read registry exchanges, with sensitive values hidden',
    registry_payload_raw_view: 'View the raw content exchanged with a registry',
    registry_credential_manage: 'Manage registry credentials and certificates',
    registry_reconcile: 'Resolve differences found when reconciling with a registry',
    billing_read: 'Read billing and the ledger',
    billing_adjust: 'Adjust a balance',
    einvoice_issue: 'Issue e-invoices, send them again and look keys up',
    einvoice_manage: 'Ask for and carry out e-invoice adjustments, replacements and cancellations (a second person approves)',
    billing_adjust_large: 'Adjust a balance by a large amount',
    auction_read: 'Read auctions, bids, backorders and expected drops',
    auction_manage: 'Create and cancel auctions, and schedule drops',
    auction_result_override: 'Override the result of an auction',
    compliance_read: 'Read compliance cases and their state',
    compliance_override: 'Override a critical compliance decision',
    compliance_legal_unlock: 'Release a legal lock',
    disclosure_read: 'Read requests for registration data',
    disclosure_manage: 'Register and triage requests for registration data',
    disclosure_decide: 'Approve or refuse a request for registration data',
    disclosure_release: 'Hand over approved non-public registration data',
    abuse_read: 'Read abuse cases, reports, evidence and domain actions',
    abuse_manage: 'Open and triage abuse cases, add evidence, ask for domain actions',
    dispute_manage: 'Open, decide and close domain disputes, including legal locks',
    escrow_read: 'Read data escrow deposits, checks, deliveries and receipts',
    escrow_manage: 'Start or abandon escrow deposits and record the agent’s receipt',
    vnnic_read: 'Read VNNIC synchronisation, reports and coverage',
    vnnic_manage: 'Produce and export VNNIC reports, and record their outcome',
    customer_manage: 'Create and manage customer accounts',
    audit_read: 'Read the audit log',
    ops_read: 'Read operational dashboards',
    ops_query: 'Query metrics, logs and traces',
    ops_admin: 'Manage alerts, silences and incidents',
    admin_users_manage: 'Manage staff accounts and roles',
    api_credential_manage: 'Issue and revoke API keys of customers',
    reporting_key_read: 'See the places that receive reporting data, their keys and use',
    reporting_key_manage: 'Manage the places that receive reporting data and their keys',
    personal_data_export: 'Approve sending customer contact data to a place that receives data',
    dns_tsig_manage: 'Set and replace the key that protects copies of a DNS zone (TSIG); the key is stored encrypted and never shown again',
    catalog_read: 'See the product catalogue: groups, products, plans, options, delivery, stock and overdue settings',
    catalog_manage: 'Edit the product catalogue (drafts): groups, products, plans, options, add-ons, delivery, stock, locations, server groups and overdue settings',
    catalog_publish: 'Put a plan on sale or take it off, and publish a plan’s new prices (on production a second person approves)',
    subscription_read: 'See sold services: list, detail, paid periods, renewal orders, the overdue calendar and the waiting-for-provisioning queue',
    subscription_operate: 'Operate sold services: suspend and unsuspend with a reason, extend up to 31 days, complete a manual delivery',
    subscription_terminate: 'Request the termination of a sold service, at once or at the end of its period',
    subscription_order: "Order a new service for a customer, held on the customer's wallet",
    subscription_extend_large: 'Extend a sold service by more than 31 days without charge',
    pricing_manage: 'Change prices, registry costs, price books and promotions',
    tax_manage: 'Publish and end VAT rates, choose the default VAT code',
    account_manage: 'Client: users, invitations, keys, connected apps (signed-in session only)',
    account_read: 'Client: account profile',
    addon_read: 'See add-ons, their state and their pages',
    addon_operate: 'Use the actions of add-on pages and the add-ons\' staff routes, and replay parked add-on events',
    addon_manage: 'Install, activate, deactivate, upgrade and uninstall add-ons and choose what happens to their data (on production, and when data is dropped, a second person approves)',
    approval_self_approve: 'Approve one\'s own integration change when no second person is available (TOTP re-confirmation, audited)',
    bulk_run: 'Client: bulk jobs',
    business_registry_lookup: 'Look an enterprise or household business up in the business registry by tax code',
    domain_destroy: 'Client: delete a domain',
    domain_protection_manage: 'Protect / unprotect domains and confirm or reject their pending changes',
    domain_transfer: 'Client: transfer code, transfer-out answer, transfer unlock, registrant and name change',
    dossier_write: 'Client: dossier files, declarations, eKYC',
    integration_manage: 'Change registry connections, TLD channels and VNNIC provider settings; test connections (PRODUCTION changes need a second approver)',
    integration_read: 'Read registry connections, TLD channels, VNNIC provider settings, connection tests and their history',
    lookup_registrant_view: 'See registrant names in registry WHOIS lookups (VNNIC infoDomain)',
    name_change_manage: 'Open name-change requests, upload their documents, record consent, submit or cancel them',
    name_change_requirements_manage: 'Publish name-change requirement sets (documents per case)',
    name_change_review: 'Approve / reject / send back name-change requests and record VNNIC refusals',
    notify_inbox_read: 'Client: notifications',
    notify_manage: 'Change e-mail settings and templates, resend notifications',
    notify_read: 'Read e-mail settings, templates and the notification log',
    order_create: 'Client: register, renew, transfer in, restore, add-ons, bids, backorders (charges the wallet)',
    promo_code_request: 'Ask the registry for a diagnostic free-program promotion code (sends registrant data)',
    reference_sync: 'Synchronise reference lists (administrative places)',
    registry_panel_manage: 'Record changes submitted in the VNNIC panel and re-check them',
    registry_tld_manage: 'Enable / disable TLDs from the built-in TLD catalogue',
    regulatory_report_manage: 'Generate / export reports to the authorities, record their outcomes with evidence, resubmit dossiers',
    regulatory_report_read: 'Read reports to the authorities, dossier submissions and coverage',
    service_manage: 'Client: auto-renew and other service settings',
    service_read: 'Client: services and subscriptions',
    tld_settings_manage: 'Publish TLD settings (transfer, restore, expiry lifecycle) and the parking name servers',
    wallet_topup: 'Client: wallet top-up (signed-in session only)',
    webhook_manage: 'Change outgoing webhook endpoints and their secrets; send test events; resend deliveries',
    webhook_read: 'Read outgoing webhook endpoints and the delivery log',
    work_item_manage: 'Assign work items and resolve exception items (review decisions need the permission “Review and complete dossiers”)',
    work_item_read: 'Read the work queue (items needing a person)',
  },

  approvalStatus: {
    PENDING: 'Waiting',
    APPROVED: 'Agreed',
    REJECTED: 'Refused',
    EXPIRED: 'Timed out',
    EXECUTED: 'Carried out',
    CANCELLED: 'Withdrawn',
  },

  approvals: {
    title: 'Approvals',
    subtitle: 'Changes that are waiting for a second operator to agree.',
    loadError: 'Could not load approvals',
    empty: 'Nothing is waiting for a decision.',
    emptyFiltered: 'No request matches these filters.',
    count: '{{count}} requests',
    filterStatus: 'State',
    statusLive: 'Waiting now',
    statusAll: 'All',
    col: {
      change: 'Requested change',
      resource: 'Applies to',
      requester: 'Asked by',
      age: 'Waiting',
      status: 'State',
      decision: 'Decision',
    },
    self: 'You asked for this',
    expiresIn: 'Times out {{when}}',
    expired: 'Timed out',
    decidedBy: 'Decided by {{name}}',
    undecided: 'Not decided yet',
    open: 'Open this request',
    review: 'Review',
    stat: {
      waiting: 'Waiting for a decision',
      yours: 'Asked by you',
      yoursHint: 'A colleague has to agree to these.',
      soon: 'Timing out within 4 hours',
      soonHint: 'If nobody decides, the request lapses and has to be raised again.',
      requesters: 'People waiting on a decision',
    },
    dialog: {
      title: 'Requested change',
      change: 'Requested change',
      resource: 'Applies to',
      requester: 'Asked by',
      requested: 'Asked at',
      expires: 'Times out',
      reason: 'Why it was asked for',
      details: 'What would change',
      status: 'State',
      decision: 'Decision',
      decidedBy: 'Decided by',
      decidedAt: 'Decided at',
      decisionReason: 'Reason given',
      yourReason: 'Your reason',
      yourReasonPlaceholder: 'What did you check before deciding?',
      reasonRequired: 'A reason is required. It is kept with the decision.',
      approve: 'Agree to the change',
      reject: 'Refuse the change',
      working: 'Recording…',
      confirmApprove: 'Agree to this change? It takes effect once you confirm, and your name stays on it.',
      confirmReject: 'Refuse this change? The person who asked will have to raise it again.',
      done: 'Recorded.',
      close: 'Close',
    },
  },

  ownerType: {
    CUSTOMER: 'Customer',
    SYSTEM: 'Platform component',
  },

  credentialStatus: {
    ACTIVE: 'Active',
    REVOKED: 'Revoked',
    EXPIRED: 'Expired',
  },

  credentials: {
    title: 'API credentials',
    subtitle: 'The keys customers and platform components use to reach the registrar, and what each one may do.',
    loadError: 'Could not load API credentials',
    empty: 'No credential matches these filters.',
    count: '{{count}} credentials',
    searchPlaceholder: 'Customer name',
    filterOwner: 'Belongs to',
    filterStatus: 'State',
    col: {
      owner: 'Belongs to',
      key: 'Key prefix',
      scopes: 'May do',
      created: 'Issued',
      lastUsed: 'Last used',
      state: 'State',
    },
    keyHint: 'The first characters of the key, enough to recognise it in a log line.',
    noScopes: 'Nothing',
    neverUsed: 'Never used',
    expiresAt: 'Expires {{when}}',
    expiredAt: 'Expired {{when}}',
    revokedAt: 'Revoked {{when}}',
    noExpiry: 'No expiry',
    component: 'Platform component',
    componentHint: 'Used by the registrar’s own software, not by a customer.',
    openOwner: 'Open {{name}}',
    stat: {
      active: 'Active keys',
      idle: 'Unused for 90 days',
      idleHint: 'Consider revoking keys nobody uses.',
      revoked: 'Revoked or expired',
      owners: 'Accounts with a key',
    },
  },

  auditOutcome: {
    SUCCESS: 'Done',
    DENIED: 'Refused',
    FAILED: 'Failed',
  },

  actorType: {
    OPERATOR: 'Staff',
    CUSTOMER: 'Customer',
    RESELLER: 'Reseller',
    SYSTEM: 'Automated',
    EXTERNAL: 'Outside party',
  },

  resourceType: {
    domain: 'Domain',
    user: 'Person',
    customer: 'Customer',
    reseller: 'Reseller',
    subject: 'Registrant',
    subject_version: 'Registrant record',
    subject_identifier: 'Identity number',
    subject_verification: 'Identity check',
    dossier: 'Dossier',
    document: 'Document',
    document_version: 'Document',
    abuse_case: 'Abuse case',
    domain_dispute: 'Dispute',
    disclosure_case: 'Disclosure request',
    law_enforcement_request: 'Law-enforcement request',
    domain_action: 'Domain action',
    registry_transaction: 'Registry command',
    ledger_account: 'Wallet',
    auction: 'Auction',
    drop: 'Expected drop',
    escrow_deposit: 'Escrow deposit',
    vnnic_report_batch: 'VNNIC report',
    vnnic_dossier_sync: 'VNNIC dossier sync',
    vnnic_sync_transaction: 'VNNIC sync',
    registrant_change: 'Registrant change',
    ops_incident: 'Incident',
    approval_request: 'Approval request',
    api_credential: 'API key',
    reporting_consumer: 'Place that receives data',
    backorder: 'Backorder',
    domain_transfer: 'Transfer',
    notification: 'Notification',
    notify_owner: 'Notification settings',
    provider_setting: 'Provider setting',
    module_instance: 'Module connection',
    addon: 'Add-on',
    einvoice_document: 'E-invoice',
    reconciliation_issue: 'Reconciliation issue',
    registry_connection: 'Registry connection',
    registry_poll_message: 'Registry poll message',
    subject_birth_date: 'Date of birth',
    vn_place_list: 'Place list',
    webhook_delivery: 'Webhook delivery',
    webhook_endpoint: 'Webhook endpoint',
    work_item: 'Work item',
    declaration: 'Declaration',
    declaration_check_settings: 'Declaration check settings',
    document_type: 'Document type',
    domain_tracking_report: 'Daily domain tracking report',
    dns_zone: 'DNS zone',
    payment_session: 'Gateway payment session',
  },

  audit: {
    title: 'Audit log',
    subtitle: 'Who did what, when and why, across the whole platform.',
    loadError: 'Could not load the audit log',
    empty: 'No entry matches these filters.',
    count: '{{count}} entries',
    filterAction: 'Action',
    filterActionPlaceholder: 'e.g. domain. or approval.decide',
    filterOutcome: 'Outcome',
    filterActor: 'Who acted',
    filterResourceType: 'Kind of record',
    filterResourceId: 'Record',
    filterResourceIdPlaceholder: 'Exact record identifier',
    filterCorrelation: 'Correlation ID',
    filterCorrelationPlaceholder: 'Groups everything from one operation',
    col: {
      time: 'When',
      actor: 'Who',
      action: 'Action',
      resource: 'Record',
      outcome: 'Outcome',
      reason: 'Reason',
    },
    automated: 'Automated',
    noReason: 'No reason recorded',
    open: 'Open this entry',
    stat: {
      entries: 'Entries shown',
      refused: 'Refused',
      refusedHint: 'Someone tried something they are not allowed to do.',
      failed: 'Failed',
      failedHint: 'The action was allowed but did not complete.',
      people: 'People involved',
    },
    drawer: {
      title: 'Audit entry',
      when: 'When',
      who: 'Who',
      accountKind: 'Account',
      action: 'Action',
      actionCode: 'Action code (technical)',
      technical: 'Technical details',
      record: 'Record',
      recordKind: 'Kind of record',
      outcome: 'Outcome',
      reason: 'Reason given',
      before: 'Before',
      after: 'After',
      noChange: 'No values were recorded for this entry.',
      correlation: 'Correlation ID',
      request: 'Request ID',
      id: 'Entry ID',
      close: 'Close',
    },
  },
} as const;
